import { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { Eyebrow } from "@/components/Eyebrow";
import { Loader2, RefreshCw, Columns3, AlertCircle, Sparkles } from "lucide-react";

export function ContentPillarsPanel({ brandId, initialPillars }) {
  const [pillars, setPillars] = useState(initialPillars || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = useCallback(async (force) => {
    if (!brandId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/brand/pillars", { brandId, force: !!force });
      setPillars(data.pillars);
    } catch (e) {
      setError(errMsg(e, "Could not generate content pillars"));
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    setPillars(initialPillars || null);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  const list = pillars || [];

  return (
    <div className="panel-card" style={{ padding: 22 }} data-testid="content-pillars-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow testId="eyebrow-pillars">Content Pillars</Eyebrow>
          <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "6px 0 0" }}>
            Recurring themes your autopilot batch spreads a week of posts across — varied but on-strategy.
          </p>
        </div>
        {list.length > 0 && !loading && (
          <button onClick={() => run(true)} className="btn-secondary font-mono" data-testid="pillars-rerun" style={{ fontSize: 11, padding: "7px 11px", flexShrink: 0 }}>
            <RefreshCw size={12} /> Regenerate
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3" data-testid="pillars-loading" style={{ padding: "26px 4px", color: "var(--tsecondary)" }}>
          <Loader2 size={18} className="spin" />
          <span className="font-body" style={{ fontSize: 13.5 }}>Building your content strategy…</span>
        </div>
      )}

      {!loading && error && (
        <div data-testid="pillars-error" style={{ marginTop: 16 }}>
          <div className="flex items-center gap-2 font-body" style={{ fontSize: 13, color: "var(--err)" }}>
            <AlertCircle size={15} /> {error}
          </div>
          <button onClick={() => run(true)} className="btn-secondary font-mono" style={{ fontSize: 11, padding: "7px 11px", marginTop: 12 }}>
            <RefreshCw size={12} /> Try again
          </button>
        </div>
      )}

      {!loading && !error && list.length === 0 && (
        <div data-testid="pillars-empty" style={{ marginTop: 18, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
          <div className="flex items-center gap-2.5" style={{ color: "var(--tsecondary)" }}>
            <Columns3 size={18} />
            <span className="font-body" style={{ fontSize: 13.5 }}>No pillars yet — build a content strategy for this brand.</span>
          </div>
          <button onClick={() => run(false)} className="btn-primary font-mono" data-testid="pillars-generate" style={{ fontSize: 12.5, padding: "10px 16px" }}>
            <Sparkles size={14} /> Generate content pillars
          </button>
        </div>
      )}

      {!loading && list.length > 0 && (
        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
          {list.map((p, i) => (
            <div key={i} data-testid={`pillar-${i}`} style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 9, border: "1px solid var(--cardborder)", background: "var(--surface)" }}>
              <span className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--orange)", flexShrink: 0, width: 18 }}>{String(i + 1).padStart(2, "0")}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="font-display" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--tprimary)" }}>{p.name}</div>
                {p.description && <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", marginTop: 2, lineHeight: 1.45 }}>{p.description}</div>}
                {p.example && <div className="font-body" style={{ fontSize: 12, color: "var(--tmuted)", marginTop: 5, fontStyle: "italic" }}>e.g. {p.example}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
